import { Box } from "@mui/material";
import Modal from "@mui/material/Modal";
import { addDays } from "date-fns";
import format from "date-fns/format";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { DateRangePicker } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import Select from "react-select";
import SearchDatePicker from "Shared/Common/DatePicker/SearchDatePicker";
import useLang from "Shared/hooks/useLanguage";
import { CrossIcon } from "Shared/Icons";
import { reactSelectSMStyle, styles } from "Utils/Common";
import { DateTimePickerStyling } from "Utils/Common/DateTimePickerStyling";
import { upsertArray } from "Utils/Common/Requisition";
import {
  FilterTypeColumns,
  isDropdownFilterType,
  isMultiSelectFilterType,
} from "Pages/DynamicGrid/filterDependentData";

function PatientSearchInput({
  column,
  setSearchValue,
  searchValue,
  setFilters,
  filters,
  loadData,
  setCurPage,
  sendRealColumnName = false,
}: any) {
  const { t } = useLang();
  const rawFilterType = String(column?.filterColumnsType ?? "")
    .trim()
    .toLowerCase();
  const isNumberFilterType =
    rawFilterType === FilterTypeColumns.NUMBER ||
    rawFilterType.startsWith(`${FilterTypeColumns.NUMBER}:`);
  const numberMaxFromConfig = (() => {
    if (!rawFilterType.startsWith(`${FilterTypeColumns.NUMBER}:`)) {
      return undefined;
    }
    const maxPart = rawFilterType.split(":")[1];
    const parsedMax = Number(maxPart);
    return Number.isFinite(parsedMax) ? parsedMax : undefined;
  })();

  const handleChange = (e: any, name?: string) => {
    console.log(name, "name", e);

    let value;
    let type;
    if (
      !isDropdownFilterType(name) &&
      !isMultiSelectFilterType(name)
    ) {
      value = e.target.value;
      type = e.target.type;
    } else if (isMultiSelectFilterType(name)) {
      // Search filters always send option.value (same as dropdown/multiselect),
      // even for dropdownvaluebylabel / multiselectvaluebylabel column types.
      value = (e || []).map((option: any) => option?.value).join(",");
      type = name;
    } else {
      value = e.value;
      type = name;
    }
    if (type === "date") {
      value = moment(value, "YYYY-MM-DD").format("MM/DD/YYYY");
    }
    if (
      isNumberFilterType &&
      type === FilterTypeColumns.NUMBER &&
      value !== ""
    ) {
      const numericValue = Number(value);
      if (Number.isFinite(numericValue)) {
        value = Math.max(0, numericValue);
        if (numberMaxFromConfig !== undefined) {
          value = Math.min(value, numberMaxFromConfig);
        }
      }
    }
    const updatedFilters = [...filters];

    const existingFilterIndex = updatedFilters.findIndex(
      (filter: any) => filter.columnName === column.filterColumns
    );

    if (existingFilterIndex !== -1) {
      updatedFilters[existingFilterIndex] = {
        ...updatedFilters[existingFilterIndex],
        filterValue: value,
      };
    } else {
      updatedFilters.push({
        columnName: column.filterColumns,
        filterValue: value,
        columnType: column.filterColumnsType,
        label: column.columnLabel,
      });
    }

    setFilters(updatedFilters);
    setSearchValue((prevValue: any) => {
      return {
        ...prevValue,
        filters: updatedFilters,
      };
    });
  };

  const getFilterValue = (columnName: any) => {
    const filter = searchValue.filters.find(
      (item: any) => item.columnName === columnName
    );
    const columnFilterType = column.filterColumnsType.toLowerCase();
    if (isMultiSelectFilterType(columnFilterType)) {
      const selectedValues = String(filter?.filterValue ?? "")
        .split(",")
        .map((val: string) => val.trim())
        .filter(Boolean);
      return options.filter((opt: any) =>
        selectedValues.includes(String(opt?.value ?? ""))
      );
    }
    if (isDropdownFilterType(columnFilterType)) {
      const option = options.find(
        (opt: any) => opt.value === filter?.filterValue
      );
      return option ? option : null;
    }
    return filter ? filter.filterValue : "";
  };

  const handleKeyPress = (e: any) => {
    if (e.key === "Enter") {
      setCurPage && setCurPage(1);
      loadData(false);
    }
  };

  let options: any = [];

  if (column && column.jsonOptionData) {
    try {
      const parsedData = JSON.parse(column.jsonOptionData);
      if (Array.isArray(parsedData) && parsedData.length > 0) {
        options = parsedData;
      }
    } catch (error) {
      console.error(t("Error parsing jsonOptionData:"), error);
    }
  }

  const containsComma = column?.filterColumnsType?.toLowerCase().includes(",");
  const commanSeparatedSecondValue = column?.filterColumnsType?.split(",")[0];
  console.log(column, "column");

  return (
    <div>
      {column?.filterColumnsType?.toLowerCase() === FilterTypeColumns.TEXT && (
        <input
          id={`PatientDynamicGrid_${column?.columnKey}`}
          type="text"
          className="form-control bg-white mb-lg-0 h-30px rounded-2 fs-8 w-100"
          placeholder={t("Search ...")}
          value={getFilterValue(column.filterColumns)}
          name={column?.columnKey}
          onChange={handleChange}
          onKeyDown={handleKeyPress}
        />
      )}
      {isNumberFilterType && (
          <input
            id={`PatientDynamicGrid_${column?.columnKey}`}
            type="number"
            className="form-control bg-white mb-lg-0 h-30px rounded-2 fs-8 w-100"
            placeholder={t("Search ...")}
            value={getFilterValue(column.filterColumns)}
            name={column?.columnKey}
            min={0}
            max={numberMaxFromConfig}
            onChange={(e: any) => {
              if (e.target.value === "") {
                handleChange(e);
                return;
              }
              const numericValue = Number(e.target.value);
              if (!Number.isFinite(numericValue)) return;
              let nextValue = Math.max(0, numericValue);
              if (numberMaxFromConfig !== undefined) {
                nextValue = Math.min(nextValue, numberMaxFromConfig);
              }
              handleChange({ ...e, target: { ...e.target, value: nextValue } });
            }}
            onKeyDown={handleKeyPress}
          />
        )}
      {isDropdownFilterType(column?.filterColumnsType) && (
          <Select
            inputId={`PatientDynamicGrid_${column?.columnKey}`}
            menuPortalTarget={document.body}
            placeholder={t("Select ...")}
            theme={(theme) => styles(theme)}
            options={options}
            name={column?.filterColumnsType}
            styles={reactSelectSMStyle}
            value={getFilterValue(column.filterColumns)}
            onChange={(e: any) => handleChange(e, column?.filterColumnsType)}
          />
        )}
      {isMultiSelectFilterType(column?.filterColumnsType) && (
          <Select
            inputId={`PatientDynamicGrid_${column?.columnKey}`}
            menuPortalTarget={document.body}
            placeholder={t("Select ...")}
            theme={(theme) => styles(theme)}
            options={options}
            name={column?.filterColumnsType}
            styles={reactSelectSMStyle}
            value={getFilterValue(column.filterColumns)}
            isMulti={true}
            isSearchable={true}
            onChange={(e: any) => handleChange(e, column?.filterColumnsType)}
          />
        )}
      {column?.filterColumnsType?.toLowerCase() ===
        FilterTypeColumns.DATEPICKER && (
          <SearchDatePicker
            value={getFilterValue(column.filterColumns)}
            onChange={handleChange}
          />
        )}
      {containsComma &&
        commanSeparatedSecondValue.toLowerCase() ===
        FilterTypeColumns.DATE_RANGE && (
          <DateRange
            sendRealColumnName={sendRealColumnName}
            column={column}
            searchValue={searchValue}
            setSearchValue={setSearchValue}
            filters={filters}
            setFilters={setFilters}
          />
        )}
      {column?.filterColumnsType?.toLowerCase() ===
        FilterTypeColumns.DATE_RANGE && (
          <DateRange
            sendRealColumnName={sendRealColumnName}
            column={column}
            searchValue={searchValue}
            setSearchValue={setSearchValue}
            filters={filters}
            setFilters={setFilters}
          />
        )}
    </div>
  );
}

export default PatientSearchInput;

const DateRange = ({ column, searchValue, setSearchValue, filters, setFilters, sendRealColumnName }: any) => {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [range, setRange] = useState([
    {
      startDate: new Date(),
      endDate: new Date(),
      key: "selection",
    },
  ]);

  const handleClose = () => {
    setOpen(false);
    setRange([
      {
        startDate: new Date(),
        endDate: new Date(),
        key: "selection",
      },
    ]);
  };

  const refOne = useRef(null);

  useEffect(() => {
    document.addEventListener("keydown", hideOnEscape, true);
  }, []);

  const hideOnEscape = (e: any) => {
    setRange([
      {
        startDate: new Date(),
        endDate: new Date(),
        key: "selection",
      },
    ]);
    if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const orientation = window.matchMedia("(max-width: 700px)").matches
    ? "vertical"
    : "horizontal";

  const setDateToInput = (searchValue: any, columnKey?: string) => {
    let filterDate = searchValue?.filters?.find(
      (filteredObj: any) =>
        filteredObj.columnName == columnKey ||
        filteredObj.columnKey == columnKey
    );

    if (filterDate) {
      return filterDate?.filterValue;
    } else {
      return "";
    }
  };

  return (
    <div>
      <input
        id={`PatientDynamicGrid_${column?.columnKey}`}
        value={setDateToInput(searchValue, column?.columnKey)}
        readOnly
        onClick={() => {
          setOpen(!open);
        }}
        className="form-control bg-white mb-lg-0 h-30px rounded-2 fs-8"
        placeholder={t("MM/DD/YYYY")}
      />

      <Modal
        open={open}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={DateTimePickerStyling}>
          <div ref={refOne}>
            {open && (
              <>
                <Box onClick={handleClose} className="icon-container">
                  <CrossIcon />
                </Box>
                <DateRangePicker
                  onChange={(item: any) => {
                    setRange([item.selection]);
                    let startDate = format(
                      item?.selection?.startDate,
                      "MM/dd/yyyy"
                    );
                    let endDate = format(
                      item?.selection?.endDate,
                      "MM/dd/yyyy"
                    );
                    console.log();

                    let concatedDate = startDate + " to " + endDate;
                    let filterObj: any = {
                      columnName: column.columnKey,
                      filterValue: concatedDate,
                      columnType: column.filterColumnsType,
                      label: column.columnLabel,
                    };
                    if (sendRealColumnName) {
                      filterObj.columnName = column.filterColumns;
                      filterObj.columnKey = column.columnKey;
                    }
                    const updatedFilters = upsertArray(
                      [...filters],
                      filterObj,
                      (element: any) =>
                        element.columnName === filterObj.columnName
                    );
                    setFilters(updatedFilters);
                    setSearchValue((preVal: any) => {
                      return {
                        ...preVal,
                        [column.columnKey]: concatedDate,
                        filters: updatedFilters,
                      };
                    });
                  }}
                  editableDateInputs={true}
                  moveRangeOnFirstSelection={false}
                  ranges={range}
                  initialFocusedRange={[] as any}
                  months={2}
                  direction="horizontal"
                  maxDate={addDays(new Date(), 0)}
                />
              </>
            )}
          </div>
          <div style={{ textAlign: "end", padding: "0% 2%" }}>
            <button onClick={handleClose} className="btn btn-primary btn-sm">
              {t("Submit")}
            </button>
          </div>
        </Box>
      </Modal>
    </div>
  );
};
